<!-- eslint-disable max-lines -->
<template>
  <div class="content">
    <div class="row align-center has-internal-nav">
      <div class="small-12 large-11 columns">
        <h1 class="content__title">
          {{ $t('FINANCE.SETTLEMENT.TITLE') }}
        </h1>

        <div class="row">
          <div
            :class="[
              'small-12',
              'columns',
              settlementRequests ? 'large-5' : 'large-12',
            ]"
          >
            <div class="card balance card--bordered card--success">
              <div class="card__block">
                <div class="card__header balance__info">
                  <div
                    v-dompurify-html="
                      $t('FINANCE.SETTLEMENT.SETTLEABLE_AMOUNT')
                    "
                  />
                  <small v-if="isProductsNonDigital">
                    {{ $t('FINANCE.SETTLEMENT.NON_DIGITAL_DESC') }}
                  </small>
                </div>

                <HollowDotsSpinner
                  v-show="isRequestingSettleableAmount"
                  class="balance__loading"
                  :animation-duration="1000"
                  :dot-size="15"
                  :dots-num="3"
                  :color="'#95a5a6'"
                />

                <div
                  v-if="!isRequestingSettleableAmount"
                  class="balance__amount"
                >
                  <span>
                    {{ currencyFormat(settleableAmount) }}
                  </span>
                  <small
                    :class="[
                      'balance__currency',
                      { invisible: !settleableAmount },
                    ]"
                  >
                    {{ $t('RIALS') }}
                  </small>
                </div>
              </div>
              <img
                class="balance__icon"
                :src="require('@/assets/images/moneybag.svg')"
              />
            </div>
          </div>

          <div v-if="settlementRequests" class="small-12 large-7 columns">
            <div class="card balance card--bordered card--success">
              <div class="card__block">
                <div class="card__header">
                  {{ $t('FINANCE.SETTLEMENT.IN-PROGRESS_REQUEST') }}
                </div>

                <div class="settlement-request">
                  <div class="settlement-request__item">
                    <span>{{ $t('FINANCE.SETTLEMENT.PAYMENT_ACCOUNT') }}:</span>
                    <span
                      v-if="$auth.check('foreigner')"
                      v-dompurify-html="settlementRequests.payment_account"
                      class="settlement-request__account settlement-request__account--foreigner text-latin"
                    />
                    <span v-else class="text-latin">
                      {{ settlementRequests.payment_account }}
                    </span>
                  </div>

                  <div class="settlement-request__item">
                    <span>{{ $t('FINANCE.SETTLEMENT.STATUS') }}:</span>
                    <span>{{ settlementRequests.get_state_display }}</span>
                  </div>

                  <div class="settlement-request__item">
                    <span>{{ $t('FINANCE.SETTLEMENT.REQUEST_DATE') }}:</span>
                    <span>{{ translateDate(settlementRequests.date) }}</span>
                  </div>

                  <div class="settlement-request__item">
                    <span>{{ $t('FINANCE.SETTLEMENT.AMOUNT') }}:</span>
                    <span>
                      {{ currencyFormat(settlementRequests.amount) }}&nbsp;{{
                        $t('RIALS')
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <InternalNavbar :navigation="navigation" :auto-hide="true" />

        <hr class="divider hide-for-large" />

        <RouterView />
      </div>
    </div>
  </div>
</template>

<script>
import layout from '@/mixins/layout';
import HollowDotsSpinner from '@@/common/HollowDotsSpinner';
import InternalNavbar from '@@/layout/InternalNavbar';
import shebaIsNotVerified from '@/utils/finance/shebaIsNotVerified';

import {
  SETTLEMENT_MISC_TRANSACTIONS,
  TAX_INVOICE_HISTORY,
} from '@/store/types';

import { Settlement } from '@/api/api';

import { format as currencyFormat } from '@/utils/number';
import { dateFormat } from '@/utils/date';
import lang from '@/mixins/lang';

export default {
  name: 'SettlementView',

  components: {
    InternalNavbar,
    HollowDotsSpinner,
  },

  mixins: [layout, lang],

  data() {
    return {
      settleableAmount: '',
      settlementRequests: null,
      isRequestingSettleableAmount: false,
    };
  },

  computed: {
    shebaIsNotVerified,

    miscTransactions() {
      return this.$store.getters[SETTLEMENT_MISC_TRANSACTIONS];
    },

    taxInvoices() {
      return this.$store.getters[TAX_INVOICE_HISTORY];
    },

    navigation() {
      const nav = [];

      nav.push({
        to: { name: 'settlement-history' },
        text: this.$t('FINANCE.SETTLEMENT_HISTORY.TITLE'),
      });

      if (
        this.$auth.check(['publisher', 'blockedPublisher', 'changeFinanceInfo'])
      ) {
        nav.push({
          to: { name: 'settlement-settings' },
          text: this.$t('FINANCE.SETTLEMENT_SETTINGS.TITLE'),
          warnBadge: this.shebaIsNotVerified,
        });
      }

      if (this.miscTransactions && this.miscTransactions.length) {
        nav.push({
          to: { name: 'settlement-misc' },
          text: this.$t('FINANCE.SETTLEMENT_MISC.TITLE'),
        });
      }

      //        if (this.taxInvoices && this.taxInvoices.length) {
      //          nav.push({
      //            to: { name: 'settlement-tax-history' },
      //            text: this.$t('FINANCE.SETTLEMENT_TAX_HISTORY.TITLE')
      //          });
      //        }

      return nav;
    },

    isProductsNonDigital() {
      const { productsType: type } = this.$auth.user.publisher || {};

      return type === 'non_digital';
    },
  },

  created() {
    this.setPageTitle('FINANCE');
    this.getSettlementAmount();
    this.getSettlementRequests();

    if (!this.miscTransactions) {
      this.getMiscTransactions();
    }

    if (!this.taxInvoices) {
      this.getTaxInvoices();
    }
  },

  methods: {
    currencyFormat,

    translateDate(date) {
      let toFormat = 'DD/MM/YYYY';

      if (this.lang === 'fa') {
        toFormat = 'jYYYY/jMM/jDD';
      }

      return dateFormat()(toFormat)(date);
    },

    getSettlementAmount() {
      this.isRequestingSettleableAmount = true;
      return Settlement.getSettleableAmount()
        .then(({ body: { settleable_balance: balance } }) => {
          this.settleableAmount = balance;
          this.isRequestingSettleableAmount = false;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    getSettlementRequests() {
      return Settlement.getSettlementRequests()
        .then(({ body: { settlement_requests: settlementRequests } }) => {
          this.settlementRequests = settlementRequests;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    getMiscTransactions() {
      return this.$store
        .dispatch(SETTLEMENT_MISC_TRANSACTIONS)
        .catch(message => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    getTaxInvoices() {
      return this.$store.dispatch(TAX_INVOICE_HISTORY).catch(message => {
        if (message) {
          this.$message.error(message);
        }
      });
    },
  },
};
</script>

<style lang="scss">
.balance {
  padding: 0;
  position: relative;
  overflow: hidden;

  &__amount {
    font-size: $global-font-size * 2;
    font-weight: 700;
    margin-top: $global-margin * 0.4;
  }

  &__info {
    small {
      color: $lb-color-secondary;
      font-size: $small-font-size;
    }
  }

  &__currency {
    color: $lb-color-secondary;
    font-size: $global-font-size * 0.5;
    font-weight: normal;
    vertical-align: 2px;
  }

  &__icon {
    position: absolute;
    width: 135px;
    opacity: 0.1;
    right: -42px;
    bottom: -17px;
    z-index: 10001; // because of weird el-loading z-index ~ 100000

    @include rtl() {
      right: auto;
      left: -42px;
    }
  }

  &__loading {
    min-height: 55px !important;
    display: flex;
    align-items: center;
  }
}

.settlement-request {
  display: flex;
  flex-wrap: wrap;

  &__account {
    &--foreigner {
      direction: ltr;
    }
  }

  &__item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-bottom: $global-margin * 0.5;

    @include breakpoint(large) {
      margin-bottom: 0;
      width: auto;

      &:nth-child(odd) {
        width: 65%;
      }

      &:nth-child(1),
      &:nth-child(2) {
        margin-bottom: $global-margin * 0.5;
      }
    }

    span {
      &:first-child {
        padding-right: 5px;
        font-weight: 700;

        @include rtl() {
          padding-left: 5px;
          padding-right: 0;
        }
      }

      &:last-child {
        font-size: $small-font-size;
      }
    }
  }
}
</style>
